/**
 * Fancy QR Code Modal with Vendor Branding
 * Uses qr-code-styling library for stylized QR codes with logo/letter overlay
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import CloseButton from './CloseButton';
import { Dialog } from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import QRCodeStyling, { Options } from 'qr-code-styling';
import html2canvas from 'html2canvas';

interface FancyQrCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  qrUrl: string; // The URL the QR code should encode
  qrName: string; // Display name (e.g., "Table 61")
  vendorName: string; // Vendor name for the letter overlay
  vendorLogo?: string; // Optional logo URL
  tableName?: string; // Table name for branded card (e.g., "Table 1", "201")
  floorName?: string; // Floor name for branded card (e.g., "Ground Floor")
  sectionName?: string; // Section name for branded card (e.g., "Main", "A/C")
}

// QR Code style presets
type QrStyle = 'classic' | 'rounded' | 'dots' | 'classy' | 'elegant' | 'branded';

const FancyQrCodeModal: React.FC<FancyQrCodeModalProps> = ({
  isOpen,
  onClose,
  qrUrl,
  qrName,
  vendorName,
  vendorLogo,
  tableName,
  floorName,
  sectionName
}) => {
  const { theme } = useTheme();
  const qrRef = useRef<HTMLDivElement>(null);
  const brandedCardRef = useRef<HTMLDivElement>(null);
  const qrCodeInstance = useRef<QRCodeStyling | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<QrStyle>('elegant');
  const [isGenerating, setIsGenerating] = useState(true);

  // Extract table number from tableName or qrName
  const displayTableName = tableName || qrName?.replace('QR Code - ', '') || 'Table';
  const locationText = [floorName, sectionName].filter(Boolean).join(' > ');

  // Get the first letter of vendor name for center display
  const vendorLetter = vendorName?.charAt(0)?.toUpperCase() || 'Q';

  // Create a canvas with the vendor letter
  const createLetterImage = (letter: string, bgColor: string, textColor: string): string => {
    const canvas = document.createElement('canvas');
    const size = 100;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      // Draw circular background
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fillStyle = bgColor;
      ctx.fill();

      // Draw letter
      ctx.fillStyle = textColor;
      ctx.font = 'bold 56px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(letter, size / 2, size / 2 + 2);
    }

    return canvas.toDataURL('image/png');
  };

  // Style configurations
  const getStyleConfig = (style: QrStyle): Partial<Options> => {
    const primaryColor = theme.primary || '#2563eb';
    const secondaryColor = theme.accent || '#1e40af';

    const baseConfig: Partial<Options> = {
      width: 280,
      height: 280,
      margin: 10,
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 8,
        imageSize: 0.4,
        saveAsBlob: true
      },
      backgroundOptions: {
        color: '#ffffff',
      },
    };

    switch (style) {
      case 'classic':
        return {
          ...baseConfig,
          dotsOptions: {
            color: '#000000',
            type: 'square'
          },
          cornersSquareOptions: {
            color: '#000000',
            type: 'square'
          },
          cornersDotOptions: {
            color: '#000000',
            type: 'square'
          }
        };

      case 'rounded':
        return {
          ...baseConfig,
          dotsOptions: {
            color: primaryColor,
            type: 'rounded'
          },
          cornersSquareOptions: {
            color: secondaryColor,
            type: 'extra-rounded'
          },
          cornersDotOptions: {
            color: primaryColor,
            type: 'dot'
          }
        };

      case 'dots':
        return {
          ...baseConfig,
          dotsOptions: {
            color: primaryColor,
            type: 'dots'
          },
          cornersSquareOptions: {
            color: secondaryColor,
            type: 'dot'
          },
          cornersDotOptions: {
            color: primaryColor,
            type: 'dot'
          }
        };

      case 'classy':
        return {
          ...baseConfig,
          dotsOptions: {
            color: '#1a1a2e',
            type: 'classy'
          },
          cornersSquareOptions: {
            color: '#16213e',
            type: 'extra-rounded'
          },
          cornersDotOptions: {
            color: '#0f3460',
            type: 'dot'
          }
        };

      case 'branded':
        // For branded style - LARGE QR for maximum scannability
        // Pure BLACK for best B&W print quality - 180px for easy scanning
        return {
          width: 180,
          height: 180,
          margin: 0,
          imageOptions: {
            crossOrigin: 'anonymous',
            margin: 0,
            imageSize: 0,  // No center image - QR needs full visibility
            saveAsBlob: true
          },
          backgroundOptions: {
            color: '#ffffff',
          },
          dotsOptions: {
            color: '#000000',  // Pure black for B&W print
            type: 'square'  // Square dots for maximum scannability
          },
          cornersSquareOptions: {
            color: '#000000',
            type: 'square'
          },
          cornersDotOptions: {
            color: '#000000',
            type: 'square'
          }
        };

      case 'elegant':
      default:
        return {
          ...baseConfig,
          dotsOptions: {
            type: 'classy-rounded',
            gradient: {
              type: 'linear',
              rotation: 45,
              colorStops: [
                { offset: 0, color: primaryColor },
                { offset: 1, color: secondaryColor }
              ]
            }
          },
          cornersSquareOptions: {
            type: 'extra-rounded',
            gradient: {
              type: 'linear',
              rotation: 45,
              colorStops: [
                { offset: 0, color: secondaryColor },
                { offset: 1, color: primaryColor }
              ]
            }
          },
          cornersDotOptions: {
            type: 'dot',
            color: primaryColor
          }
        };
    }
  };

  // Generate QR code
  useEffect(() => {
    if (!isOpen) return;
    
    // Check for valid QR URL
    if (!qrUrl || qrUrl === '' || qrUrl === 'null' || qrUrl === 'undefined') {
      console.warn('FancyQrCodeModal: Invalid qrUrl:', qrUrl);
      setIsGenerating(false);
      return;
    }

    console.log('FancyQrCodeModal: Generating QR for URL:', qrUrl, 'Style:', selectedStyle);
    setIsGenerating(true);
    
    // Clear previous QR instance to force regeneration
    if (qrCodeInstance.current) {
      qrCodeInstance.current = null;
    }

    const generateQr = async () => {
      // Longer delay to ensure DOM is fully ready after style change
      await new Promise(resolve => setTimeout(resolve, 200));

      if (!qrRef.current) {
        console.warn('QR container ref not found');
        setIsGenerating(false);
        return;
      }

      try {
        // Aggressively clear any previous QR code elements from everywhere
        qrRef.current.innerHTML = '';
        
        // Remove any stray canvas or SVG elements from the entire modal
        const modalContainer = qrRef.current.closest('.flex.items-center.justify-center');
        if (modalContainer) {
          const allCanvases = modalContainer.querySelectorAll('canvas');
          allCanvases.forEach(canvas => {
            if (!qrRef.current?.contains(canvas)) {
              canvas.remove();
            }
          });
        }

        const styleConfig = getStyleConfig(selectedStyle);
        
        // For branded style, don't include center image - QR needs to be compact and fully visible
        const letterImage = selectedStyle === 'branded' 
          ? undefined 
          : (vendorLogo || createLetterImage(vendorLetter, theme.primary || '#2563eb', '#ffffff'));

        const options: Options = {
          ...styleConfig,
          data: qrUrl,
          ...(letterImage ? { image: letterImage } : {}),
        } as Options;

        console.log('FancyQrCodeModal: Creating QR with data:', qrUrl);
        qrCodeInstance.current = new QRCodeStyling(options);
        qrCodeInstance.current.append(qrRef.current);
        console.log('FancyQrCodeModal: QR appended to DOM');
      } catch (err) {
        console.error('Failed to generate QR code:', err);
      } finally {
        // Ensure loading state is cleared
        setTimeout(() => setIsGenerating(false), 300);
      }
    };

    generateQr();

    // Cleanup function - remove QR when dependencies change
    return () => {
      if (qrRef.current) {
        qrRef.current.innerHTML = '';
      }
      qrCodeInstance.current = null;
    };

  }, [isOpen, qrUrl, selectedStyle, vendorLetter, vendorLogo, theme.primary, theme.accent]);

  // Download branded card as image
  const handleDownloadBrandedCard = useCallback(async () => {
    if (!brandedCardRef.current) return;
    
    try {
      const canvas = await html2canvas(brandedCardRef.current, {
        backgroundColor: '#1a1a2e',
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
      });
      
      const link = document.createElement('a');
      link.download = `${displayTableName.replace(/\s+/g, '-')}-qr-card.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to download branded card:', error);
    }
  }, [displayTableName]);

  // Print branded card
  const handlePrintBrandedCard = useCallback(async () => {
    if (!brandedCardRef.current) return;
    
    try {
      const canvas = await html2canvas(brandedCardRef.current, {
        backgroundColor: '#1a1a2e',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imageUrl = canvas.toDataURL('image/png');
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print QR Card - ${displayTableName}</title>
            <style>
              body {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100vh;
                margin: 0;
                background: #f5f5f5;
              }
              img {
                max-width: 90%;
                max-height: 90vh;
                box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                border-radius: 16px;
              }
            </style>
          </head>
          <body>
            <img src="${imageUrl}" alt="${displayTableName} QR Card" />
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); }, 500);
              }
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Failed to print branded card:', error);
    }
  }, [displayTableName]);

  const handleDownload = () => {
    if (selectedStyle === 'branded') {
      handleDownloadBrandedCard();
      return;
    }
    
    if (qrCodeInstance.current) {
      qrCodeInstance.current.download({
        name: `${qrName || 'qrcode'}-fancy`,
        extension: 'png'
      });
    }
  };

  const handlePrint = async () => {
    if (selectedStyle === 'branded') {
      handlePrintBrandedCard();
      return;
    }
    
    if (!qrCodeInstance.current) return;

    try {
      const blob = await qrCodeInstance.current.getRawData('png');
      if (!blob) return;

      const imageUrl = URL.createObjectURL(blob);
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print QR Code - ${qrName || 'QR Code'}</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
              }
              img {
                max-width: 400px;
                max-height: 400px;
              }
              h2 {
                margin-bottom: 20px;
                color: #1e293b;
              }
              .vendor {
                font-size: 14px;
                color: #64748b;
                margin-top: 10px;
              }
            </style>
          </head>
          <body>
            <h2>${qrName || 'QR Code'}</h2>
            <img src="${imageUrl}" alt="${qrName || 'QR Code'}" />
            <p class="vendor">${vendorName}</p>
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); }, 500);
              }
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Failed to print QR code:', error);
    }
  };

  if (!isOpen) return null;

  const styles: { id: QrStyle; name: string; icon: string }[] = [
    { id: 'elegant', name: 'Elegant', icon: '✨' },
    { id: 'rounded', name: 'Rounded', icon: '⬤' },
    { id: 'dots', name: 'Dots', icon: '●' },
    { id: 'classy', name: 'Classy', icon: '◆' },
    { id: 'classic', name: 'Classic', icon: '■' },
    { id: 'branded', name: 'Branded', icon: '🎨' },
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          m: 2, // Add margin to prevent full-width on small screens from touching edges
          border: '1px solid #e2e8f0', // Re-add border from original style
          background: 'white', // Re-add background from original style
        }
      }}
    >
      <div className="flex flex-col h-full max-h-[90vh]">
        {/* Header */}
        <div
          className="px-5 py-4 relative"
          style={{
            background: `linear-gradient(135deg, ${theme.button.secondary.bg} 0%, ${theme.background} 100%)`,
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                {qrName || 'QR Code'}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
                {vendorName}
              </p>
            </div>
            <CloseButton onClick={onClose} size="md" />
          </div>
        </div>

        {/* Style Selector */}
        <div className="px-5 py-3 border-b" style={{ borderColor: '#e2e8f0' }}>
          <div className="flex gap-2 justify-center flex-wrap">
            {styles.map((style) => (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedStyle === style.id
                  ? 'ring-2 ring-offset-1'
                  : 'hover:bg-gray-100'
                  }`}
                style={{
                  backgroundColor: selectedStyle === style.id ? theme.button.primary.bg : 'transparent',
                  color: selectedStyle === style.id ? theme.button.primary.text : '#64748b',
                  ringColor: selectedStyle === style.id ? theme.primary : 'transparent'
                }}
              >
                <span className="mr-1">{style.icon}</span>
                {style.name}
              </button>
            ))}
          </div>
        </div>

        {/* QR Code Display */}
        <div className="p-5 overflow-y-auto flex-1" style={{ background: selectedStyle === 'branded' ? '#f1f5f9' : 'white' }}>
          <div key={`qr-container-${selectedStyle}`} className="flex items-center justify-center">
            {/* Show error if no valid QR URL */}
            {(!qrUrl || qrUrl === '') ? (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <svg className="w-16 h-16 mb-4" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#1e293b' }}>No QR Code Available</h3>
                <p className="text-sm" style={{ color: '#64748b' }}>
                  This table doesn't have a QR code yet.<br />
                  Please generate a QR code first using the "Generate QR Code" button.
                </p>
              </div>
            ) : selectedStyle === 'branded' ? (
              /* Lucky Restaurant Branded Card - HYBRID: Branded + Practical B&W */
              <div 
                ref={brandedCardRef}
                className="relative"
                style={{
                  width: '340px',
                  height: '500px',
                  background: '#ffffff',
                  border: '3px solid #000000',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '15px 20px',
                  textAlign: 'center',
                }}
              >
                {/* Cloche Logo with Steam */}
                <div style={{ marginBottom: '5px' }}>
                  {/* Steam lines */}
                  <svg width="50" height="15" viewBox="0 0 60 20" style={{ marginBottom: '-3px' }}>
                    <path d="M18 16 Q15 10 18 6 Q21 2 18 -2" stroke="#000000" strokeWidth="2" fill="none" strokeLinecap="round"/>
                    <path d="M30 18 Q26 12 30 6 Q34 0 30 -4" stroke="#000000" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                    <path d="M42 16 Q45 10 42 6 Q39 2 42 -2" stroke="#000000" strokeWidth="2" fill="none" strokeLinecap="round"/>
                  </svg>
                  {/* Cloche */}
                  <svg width="55" height="40" viewBox="0 0 70 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="35" cy="7" rx="5" ry="4" fill="#000000"/>
                    <path d="M12 40 Q12 16 35 12 Q58 16 58 40" fill="#000000"/>
                    <ellipse cx="35" cy="38" rx="26" ry="6" fill="#000000"/>
                    <ellipse cx="35" cy="45" rx="28" ry="5" fill="#000000"/>
                    <text x="35" y="30" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="bold" fontFamily="Georgia, serif" fontStyle="italic">E&M</text>
                  </svg>
                </div>

                {/* Restaurant Name */}
                <p style={{
                  fontSize: '22px',
                  fontWeight: '900',
                  color: '#000000',
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '2px',
                  margin: '0 0 3px 0',
                }}>
                  LUCKY RESTAURANT
                </p>

                {/* Decorative Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '10px',
                }}>
                  <div style={{ width: '50px', height: '1px', background: '#000000' }} />
                  <span style={{ color: '#000000', fontSize: '10px' }}>★</span>
                  <div style={{ width: '50px', height: '1px', background: '#000000' }} />
                </div>

                {/* QR Code - LARGE */}
                <div
                  style={{
                    padding: '8px',
                    background: '#ffffff',
                    marginBottom: '10px',
                  }}
                >
                  {isGenerating && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
                      <svg className="animate-spin h-8 w-8" style={{ color: '#000000' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                  <div ref={qrRef} style={{ width: '180px', height: '180px', overflow: 'hidden' }} />
                </div>

                {/* Decorative Divider */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <div style={{ width: '50px', height: '1px', background: '#000000' }} />
                  <span style={{ color: '#000000', fontSize: '10px' }}>★</span>
                  <div style={{ width: '50px', height: '1px', background: '#000000' }} />
                </div>
                
                {/* Scan2Order */}
                <p style={{
                  fontSize: '18px',
                  fontWeight: '800',
                  color: '#000000',
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '1px',
                  margin: '0 0 10px 0',
                }}>
                  Scan2Order
                </p>

                {/* Table Info with border */}
                <div style={{
                  border: '2px solid #000000',
                  padding: '8px 25px',
                  borderRadius: '20px',
                  marginBottom: '5px',
                }}>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: '900',
                    color: '#000000',
                    fontFamily: 'Georgia, serif',
                    letterSpacing: '1px',
                    margin: 0,
                  }}>
                    {displayTableName}
                  </p>
                </div>
                
                {/* Location */}
                {locationText && (
                  <p style={{
                    fontSize: '11px',
                    color: '#000000',
                    fontFamily: 'Georgia, serif',
                    margin: '3px 0 0 0',
                  }}>
                    {locationText}
                  </p>
                )}
              </div>
            ) : (
              /* Regular QR Code Display */
            <div className="flex flex-col items-center">
              <div
                className="p-4 bg-white rounded-xl border-2 relative"
                style={{ borderColor: theme.button?.secondary?.hover || theme.primary }}
              >
                {isGenerating && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 rounded-xl z-10">
                    <svg className="animate-spin h-8 w-8" style={{ color: theme.primary }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                <div ref={qrRef} className="flex items-center justify-center" />
              </div>
              <p className="mt-3 text-sm text-center" style={{ color: '#64748b' }}>
                Scan to order from <span style={{ color: theme.primary, fontWeight: 500 }}>{vendorName}</span>
              </p>
            </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div
          className="px-5 py-3 flex justify-center gap-2"
          style={{
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0'
          }}
        >
          <button
            onClick={handleDownload}
            disabled={isGenerating}
            className="px-4 py-2 rounded-md text-sm font-semibold transition-colors flex items-center disabled:opacity-50"
            style={{
              backgroundColor: theme.button?.secondary?.bg || theme.background,
              border: `1px solid ${theme.primary}`,
              color: theme.primary,
            }}
            onMouseEnter={(e) => {
              if (!isGenerating) e.currentTarget.style.backgroundColor = theme.button?.secondary?.hover || theme.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.button?.secondary?.bg || theme.background;
            }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </button>
          <button
            onClick={handlePrint}
            disabled={isGenerating}
            className="px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center disabled:opacity-50"
            style={{
              backgroundColor: `${theme.accent}22`,
              border: `1px solid ${theme.accent}`,
              color: theme.accent,
            }}
            onMouseEnter={(e) => {
              if (!isGenerating) e.currentTarget.style.backgroundColor = `${theme.accent}44`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.accent}22`;
            }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default FancyQrCodeModal;
